import React, { useState, useMemo, useCallback } from "react";
import { Drawer } from "antd";
import { useIsMobile } from "./helpers";
import { ChatThemeCtx, ct } from "./MessageParts";
import { ChatPanelHeader } from "./ChatPanelHeader";
import { ChatPanelMessages } from "./ChatPanelMessages";

const DRAWER_MIN = 400;
const DRAWER_MAX = Math.round(DRAWER_MIN * 1.8); // 720

export function ActiveChatDrawer({ chat, pinned, onPinToggle, onClose, onWidthChange, onChatPatch, onQueryCreated }) {
  const isMobile = useIsMobile();
  const [drawerWidth, setDrawerWidth] = useState(DRAWER_MIN);
  const [resizing, setResizing] = useState(false);
  const [isDark, setIsDark] = useState(() => localStorage.getItem("wati_chat_theme") !== "light");
  const [chatImages, setChatImages] = useState([]);

  const theme = ct(isDark);

  const toggleTheme = useCallback(() => setIsDark(d => {
    const next = !d;
    localStorage.setItem("wati_chat_theme", next ? "dark" : "light");
    return next;
  }), []);

  const drawerStyles = useMemo(() => ({
    header: {
      padding: "10px 16px",
      borderBottom: `1px solid ${theme.border}`,
      background: theme.surface,
      transition: "background 0.35s ease, border-color 0.35s ease",
    },
    body: {
      padding: 0,
      display: "flex",
      flexDirection: "column",
      height: "100%",
      overflow: "hidden",
      background: theme.bg,
      position: "relative",
      userSelect: resizing ? "none" : undefined,
      transition: "background 0.35s ease",
    },
  }), [theme.border, theme.surface, theme.bg, resizing]); // eslint-disable-line

  const handleResizeStart = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = drawerWidth;
    setResizing(true);

    const onMove = (e) => {
      const next = Math.min(DRAWER_MAX, Math.max(DRAWER_MIN, startWidth + (startX - e.clientX)));
      setDrawerWidth(next);
      onWidthChange?.(next);
    };
    const onUp = () => {
      setResizing(false);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <ChatThemeCtx.Provider value={isDark}>
      <Drawer open={!!chat} onClose={onClose} mask={!pinned} maskClosable={!pinned} closable={false}
        push={false}
        width={isMobile ? "100%" : drawerWidth} placement="right"
        styles={drawerStyles}
        title={chat && <ChatPanelHeader chat={chat} pinned={pinned} onPinToggle={onPinToggle} onClose={onClose} isMobile={isMobile} onChatPatch={onChatPatch} isDark={isDark} onThemeToggle={toggleTheme} imageMessages={chatImages} onQueryCreated={onQueryCreated} />}>
        {!isMobile && (
          <div
            onMouseDown={handleResizeStart}
            style={{
              position: "absolute", left: 0, top: 0, bottom: 0, width: 6,
              cursor: "col-resize", zIndex: 10,
              background: resizing ? "rgba(255,255,255,0.08)" : "transparent",
              transition: "background 0.15s",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.08)"; }}
            onMouseLeave={e => { if (!resizing) e.currentTarget.style.background = "transparent"; }}
          />
        )}
        <div className="cpt" style={{ display: "contents" }}>
          {chat && <ChatPanelMessages chat={chat} onImageMessagesChange={setChatImages} />}
        </div>
      </Drawer>
    </ChatThemeCtx.Provider>
  );
}
